import { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import userService from "../services/userService";
import clientService from "../services/clientService";
import authService from "../services/authService";
import "../css/Admin.css";
import Cookies from "js-cookie";
import { getBaseUrl } from '../utils/apiHelper';
import ReactSelect from "react-select";

// Toast notification component using React Portal
const Toast = ({ message, type, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(onClose, 4000);
        return () => clearTimeout(timer);
    }, [onClose]);

    return ReactDOM.createPortal(
        <div className={`admin-toast ${type}`}>
            <span className="admin-toast-icon">
                {type === 'success' ? '✓' : '✕'}
            </span>
            <span>{message}</span>
            <button className="admin-toast-close" onClick={onClose}>×</button>
        </div>,
        document.body
    );
};

// User Card Component
const UserCard = ({
    user,
    clients,
    associations,
    adminStatuses,
    currentUserId,
    isCurrentUserSuperAdmin,
    onAssociationChange,
    onAdminStatusChange,
    onUpdate,
    onDelete
}) => {
    const isCurrentUser = user.id === currentUserId;
    const isAdmin = adminStatuses[user.id];
    const userClients = associations[user.id] || [];

    const clientOptions = clients.map(client => ({
        value: client.id,
        label: client.name
    }));

    const selectedClients = userClients.map(cid => {
        const client = clients.find(c => c.id === cid);
        return client ? { value: client.id, label: client.name } : { value: cid, label: cid };
    });

    return (
        <div className={`admin-user-card ${isAdmin ? 'is-admin' : ''} ${isCurrentUser ? 'is-current-user' : ''}`}>
            <div className="admin-card-header">
                <div className="admin-user-info">
                    <h3 className="admin-user-name">
                        {user.name}
                        {isCurrentUser && <span className="admin-badge you">You</span>}
                    </h3>
                    <p className="admin-user-email">{user.email}</p>
                </div>
                <span className={`admin-badge ${isAdmin ? 'admin' : 'user'}`}>
                    {isAdmin ? '🛡️ Admin' : '👤 User'}
                </span>
            </div>

            <div className="admin-card-body">
                {/* Client Association */}
                <div className="admin-form-group">
                    <label className="admin-form-label">Assigned Clients</label>
                    <ReactSelect
                        isMulti
                        options={clientOptions}
                        value={selectedClients}
                        onChange={(selected) => {
                            onAssociationChange(user.id, selected.map(opt => opt.value));
                        }}
                        placeholder="Select clients..."
                        classNamePrefix="react-select"
                        isClearable={false}
                        noOptionsMessage={() => "No clients available"}
                    />
                    {userClients.length === 0 && (
                        <p className="admin-no-clients">No clients assigned</p>
                    )}
                </div>

                {/* Admin Status */}
                <div className="admin-form-group">
                    <label className="admin-form-label">Role</label>
                    <select
                        className="admin-select"
                        value={isAdmin ? "true" : "false"}
                        onChange={(e) => onAdminStatusChange(user.id, e.target.value)}
                    >
                        <option value="false">👤 Regular User</option>
                        <option value="true">🛡️ Administrator</option>
                    </select>
                </div>

                {/* Actions */}
                <div className="admin-card-actions">
                    <button
                        className="admin-btn update"
                        onClick={() => onUpdate(user.id, associations[user.id], adminStatuses[user.id])}
                    >
                        💾 Save Changes
                    </button>
                    {isCurrentUserSuperAdmin && !isCurrentUser && (
                        <button
                            className="admin-btn delete"
                            onClick={() => onDelete(user.id, user.name || user.email)}
                        >
                            🗑️
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const Admin = () => {
    // State management
    const [users, setUsers] = useState([]);
    const [clients, setClients] = useState([]);
    const [associations, setAssociations] = useState({});
    const [adminStatuses, setAdminStatuses] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [isCurrentUserSuperAdmin, setIsCurrentUserSuperAdmin] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [toast, setToast] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'admins', 'assigned', 'unassigned'

    const showToast = useCallback((message, type) => {
        setToast({ message, type });
    }, []);

    const closeToast = useCallback(() => {
        setToast(null);
    }, []);

    // Initial data fetching
    useEffect(() => {
        const fetchClientsAndUsers = async () => {
            setIsLoading(true);
            try {
                const fetchedClients = await clientService.getAllClients();
                setClients(fetchedClients);

                const userData = await userService.getAllUsers();

                if (!Array.isArray(userData)) {
                    throw new Error("Invalid data format received from server");
                }

                const sortedUsers = userData.sort((a, b) => a.name.localeCompare(b.name));
                setUsers(sortedUsers);
                setFilteredUsers(sortedUsers);

                const initialAssociations = {};
                const initialAdminStatuses = {};
                sortedUsers.forEach(user => {
                    initialAssociations[user.id] = Array.isArray(user.client_ids)
                        ? user.client_ids
                        : (user.client_ids ? [user.client_ids] : []);
                    initialAdminStatuses[user.id] = Boolean(user.isAdmin);
                });
                setAssociations(initialAssociations);
                setAdminStatuses(initialAdminStatuses);

                const userId = Cookies.get("userId");
                setCurrentUserId(userId);

                const isAdmin = Cookies.get("isAdmin") === "true";
                setIsCurrentUserSuperAdmin(isAdmin);

                sessionStorage.removeItem('admin404Retried');
            } catch (error) {
                const msg = error?.message || '';
                const is404 = /404/.test(msg);
                if (is404 && !sessionStorage.getItem('admin404Retried')) {
                    sessionStorage.setItem('admin404Retried', 'true');
                    setTimeout(() => fetchClientsAndUsers(), 400);
                    return;
                }
                showToast(is404 ? 'Data not found. Please refresh.' : `Error: ${msg}`, 'error');
            } finally {
                setIsLoading(false);
            }
        };

        fetchClientsAndUsers();
    }, [showToast]);

    // Handler functions
    const handleAssociationChange = (userId, clientIds) => {
        setAssociations(prev => ({ ...prev, [userId]: clientIds }));
    };

    const handleAdminStatusChange = (userId, isAdmin) => {
        setAdminStatuses(prev => ({ ...prev, [userId]: isAdmin === "true" }));
    };

    const handleDeleteUser = async (targetUserId, userName) => {
        if (!isCurrentUserSuperAdmin) {
            showToast("Only administrators can delete users.", "error");
            return;
        }

        if (targetUserId === currentUserId) {
            showToast("You cannot delete your own account.", "error");
            return;
        }

        const isConfirmed = window.confirm(
            `Delete user "${userName}"?\n\nThis will also delete all their invoices.\nThis action cannot be undone.`
        );

        if (!isConfirmed) return;

        try {
            const response = await authService.authenticatedFetch(`${getBaseUrl()}/api/users/${targetUserId}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to delete user.");
            }

            setUsers(prev => prev.filter(user => user.id !== targetUserId));
            setFilteredUsers(prev => prev.filter(user => user.id !== targetUserId));
            showToast(`User "${userName}" deleted successfully.`, "success");
        } catch (error) {
            showToast(`Failed to delete user: ${error.message}`, "error");
        }
    };

    const handleUpdateUser = async (targetUserId, clientIds, isAdmin) => {
        if (!targetUserId) {
            showToast("Missing user ID. Cannot update.", "error");
            return;
        }

        try {
            const endpoint = `${getBaseUrl()}/api/users/${targetUserId}/client`;
            const requestBody = {
                targetUserId: targetUserId,
                clientIds: Array.isArray(clientIds) ? clientIds : [],
                isAdmin: isAdmin === true || isAdmin === "true"
            };

            const response = await authService.authenticatedFetch(endpoint, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to update user.");
            }

            const data = await response.json();
            const updatedUser = data.user || {
                id: targetUserId,
                isAdmin: requestBody.isAdmin,
                client_ids: requestBody.clientIds
            };

            setUsers(prevUsers =>
                prevUsers.map(user =>
                    user.id === targetUserId
                        ? { ...user, client_ids: updatedUser.client_ids, isAdmin: updatedUser.isAdmin }
                        : user
                )
            );
            setAssociations(prev => ({ ...prev, [targetUserId]: updatedUser.client_ids || [] }));
            setAdminStatuses(prev => ({ ...prev, [targetUserId]: updatedUser.isAdmin }));

            const user = users.find(u => u.id === targetUserId);
            showToast(`${user?.name || 'User'} updated successfully!`, "success");
        } catch (error) {
            showToast(`Failed to update: ${error.message}`, "error");
        }
    };

    // Search and filter functionality
    const handleSearch = useCallback(() => {
        const query = searchQuery.toLowerCase();
        let results = users.filter(user =>
            (user.name?.toLowerCase().includes(query)) ||
            (user.email?.toLowerCase().includes(query))
        );

        // Apply active filter
        if (activeFilter === 'admins') {
            results = results.filter(user => adminStatuses[user.id]);
        } else if (activeFilter === 'assigned') {
            results = results.filter(user => (associations[user.id] || []).length > 0);
        } else if (activeFilter === 'unassigned') {
            results = results.filter(user => (associations[user.id] || []).length === 0);
        }

        setFilteredUsers(results);
    }, [users, searchQuery, activeFilter, adminStatuses, associations]);

    useEffect(() => {
        handleSearch();
    }, [handleSearch]);

    // Handle filter click
    const handleFilterClick = (filter) => {
        setActiveFilter(activeFilter === filter ? 'all' : filter);
    };

    // Calculate stats
    const totalUsers = users.length;
    const totalAdmins = users.filter(u => adminStatuses[u.id]).length;
    const usersWithClients = users.filter(u => (associations[u.id] || []).length > 0).length;

    // Loading state
    if (isCurrentUserSuperAdmin === null) {
        return (
            <div className="admin-checking">
                <div className="admin-checking-icon">🔐</div>
                <h3>Checking Permissions</h3>
                <p>Verifying your admin access...</p>
            </div>
        );
    }

    // Access denied
    if (isCurrentUserSuperAdmin === false) {
        return (
            <div className="admin-access-denied">
                <div className="admin-access-denied-icon">🔒</div>
                <h2>Access Denied</h2>
                <p>You must be an administrator to access this page.</p>
            </div>
        );
    }

    return (
        <div className="admin-page">
            {/* Header */}
            <header className="admin-header">
                <div className="admin-header-content">
                    <div className="admin-title-section">
                        <div>
                            <h1 className="admin-title">User Management</h1>
                            <p className="admin-subtitle">Manage user roles and client assignments</p>
                        </div>
                    </div>
                </div>
            </header>

            {/* Stats Cards - Clickable Filters */}
            <div className="admin-stats">
                <button
                    className={`admin-stat-card clickable ${activeFilter === 'all' ? 'active' : ''}`}
                    onClick={() => handleFilterClick('all')}
                    title="Show all users"
                >
                    <div className="admin-stat-icon users">👥</div>
                    <div className="admin-stat-info">
                        <h4>{totalUsers}</h4>
                        <p>Total Users</p>
                    </div>
                    {activeFilter === 'all' && <span className="filter-active-indicator" />}
                </button>
                <button
                    className={`admin-stat-card clickable ${activeFilter === 'admins' ? 'active' : ''}`}
                    onClick={() => handleFilterClick('admins')}
                    title="Show only administrators"
                >
                    <div className="admin-stat-icon admins">🛡️</div>
                    <div className="admin-stat-info">
                        <h4>{totalAdmins}</h4>
                        <p>Administrators</p>
                    </div>
                    {activeFilter === 'admins' && <span className="filter-active-indicator" />}
                </button>
                <button
                    className={`admin-stat-card clickable ${activeFilter === 'assigned' ? 'active' : ''}`}
                    onClick={() => handleFilterClick('assigned')}
                    title="Show users with client assignments"
                >
                    <div className="admin-stat-icon assigned">✅</div>
                    <div className="admin-stat-info">
                        <h4>{usersWithClients}</h4>
                        <p>Users Assigned</p>
                    </div>
                    {activeFilter === 'assigned' && <span className="filter-active-indicator" />}
                </button>
            </div>

            {/* Active Filter Indicator */}
            {activeFilter !== 'all' && (
                <div className="admin-filter-indicator">
                    <span className="filter-label">
                        Filtering by: <strong>{activeFilter === 'admins' ? 'Administrators' : 'Users with Assignments'}</strong>
                    </span>
                    <button className="clear-filter-btn" onClick={() => setActiveFilter('all')}>
                        ✕ Clear Filter
                    </button>
                </div>
            )}

            {/* Search */}
            <div className="admin-search-section">
                <div className="admin-search-wrapper">
                    <input
                        type="text"
                        className="admin-search-input"
                        placeholder="Search users by name or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <span className="admin-search-icon">🔍</span>
                </div>
            </div>

            {/* User Cards */}
            {isLoading ? (
                <div className="admin-loading">
                    <div className="admin-spinner"></div>
                    <span>Loading users...</span>
                </div>
            ) : filteredUsers.length === 0 ? (
                <div className="admin-empty">
                    <div className="admin-empty-icon">👤</div>
                    <h3>{searchQuery ? 'No matches found' : 'No users yet'}</h3>
                    <p>{searchQuery ? 'Try a different search term' : 'Users will appear here once registered'}</p>
                </div>
            ) : (
                <div className="admin-users-grid">
                    {filteredUsers.map(user => (
                        <UserCard
                            key={user.id}
                            user={user}
                            clients={clients}
                            associations={associations}
                            adminStatuses={adminStatuses}
                            currentUserId={currentUserId}
                            isCurrentUserSuperAdmin={isCurrentUserSuperAdmin}
                            onAssociationChange={handleAssociationChange}
                            onAdminStatusChange={handleAdminStatusChange}
                            onUpdate={handleUpdateUser}
                            onDelete={handleDeleteUser}
                        />
                    ))}
                </div>
            )}

            {/* Toast Notification */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={closeToast}
                />
            )}
        </div>
    );
};

export default Admin;
